import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  Plane, 
  Calendar, 
  LogOut, 
  MapPin,
  Clock,
  User,
  Eye,
  BookOpen,
  MessageSquare,
  Send,
  FileText,
  Key,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({});
  const [myTrips, setMyTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State per richiesta preventivo
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteRequest, setQuoteRequest] = useState({
    destination: '',
    trip_type: 'cruise',
    start_date: '',
    end_date: '',
    travelers: 2,
    budget_range: '',
    special_requirements: '',
    contact_preference: 'email'
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch stats and trips
      const [statsRes, tripsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/trips/with-details`)
      ]);

      setStats(statsRes.data);
      setMyTrips(tripsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Attivo';
      case 'draft':
        return 'Bozza';
      case 'completed':
        return 'Completato';
      case 'cancelled':
        return 'Annullato';
      default:
        return status;
    }
  };

  const handleSubmitQuoteRequest = async () => {
    if (!quoteRequest.destination || !quoteRequest.start_date) {
      toast.error('Compila almeno destinazione e data di partenza');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/quote-requests`, {
        ...quoteRequest,
        client_id: user.id
      });
      
      toast.success('Richiesta preventivo inviata con successo!');
      setShowQuoteForm(false);
      setQuoteRequest({
        destination: '',
        trip_type: 'cruise',
        start_date: '',
        end_date: '',
        travelers: 2,
        budget_range: '',
        special_requirements: '',
        contact_preference: 'email'
      });
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error('Errore nell\'invio della richiesta');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_e899d787-232a-49fb-887e-eb0377fd3380/artifacts/dy8a9fcf_trib%C3%B9%20in%20viaggio.png" 
                alt="Tribù in Viaggio" 
                className="h-10 sm:h-12 w-auto mr-3"
              />
              <h1 className="text-lg sm:text-xl font-bold text-slate-800">I Miei Viaggi</h1>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              <span className="text-xs sm:text-sm text-slate-600 hidden sm:inline">
                {user?.first_name} {user?.last_name}
              </span>
              <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs sm:text-sm">
                Cliente
              </Badge>
              <Link to="/my-gift-lists" data-testid="my-gift-lists-nav-link">
                <Button variant="outline" size="sm" className="flex items-center gap-2 text-pink-700 border-pink-300 bg-pink-50 hover:bg-pink-100">
                  <Heart className="w-4 h-4" />
                  <span className="hidden sm:inline">Le mie liste</span>
                </Button>
              </Link>
              <Link to="/change-password">
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  <span className="hidden sm:inline">Password</span>
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="flex items-center gap-2 text-sm sm:text-base px-3 sm:px-4 py-2"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Esci</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
            Benvenuto, {user?.first_name}!
          </h2>
          <p className="text-sm sm:text-base text-slate-600">
            Gestisci i tuoi viaggi e condividi i tuoi ricordi con Tribù in Viaggio
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium text-blue-100">
                I Miei Viaggi
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="flex items-center">
                <Plane className="h-6 w-6 sm:h-8 sm:w-8 text-blue-200 mr-3" />
                <div className="text-xl sm:text-2xl font-bold">{stats.my_trips || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium text-green-100">
                Viaggi Futuri
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="flex items-center">
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-green-200 mr-3" />
                <div className="text-xl sm:text-2xl font-bold">{stats.upcoming_trips || 0}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Richiesta Preventivo */}
        <Card className="mb-6 sm:mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <MessageSquare className="w-5 h-5 text-teal-600" />
              Richiedi un Preventivo
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Hai in mente una destinazione? Richiedi un preventivo personalizzato per il tuo prossimo viaggio
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showQuoteForm ? (
              <div className="text-center py-6 sm:py-8">
                <div className="bg-teal-50 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-teal-600" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-2">
                  Pianifica il tuo prossimo viaggio
                </h3>
                <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
                  Raccontaci dove vorresti andare e ti prepareremo un preventivo su misura
                </p>
                <Button 
                  onClick={() => setShowQuoteForm(true)}
                  className="flex items-center gap-2 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
                >
                  <Send className="w-4 h-4" />
                  <span className="hidden sm:inline">Richiedi Preventivo</span>
                  <span className="sm:hidden">Preventivo</span>
                </Button>
              </div>
            ) : (
              <QuoteRequestForm 
                quoteRequest={quoteRequest}
                setQuoteRequest={setQuoteRequest}
                onSubmit={handleSubmitQuoteRequest}
                onCancel={() => setShowQuoteForm(false)}
                loading={loading}
              />
            )}
          </CardContent>
        </Card>

        {/* My Trips */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">I Miei Viaggi</CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Tutti i tuoi viaggi organizzati dalla nostra agenzia
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {myTrips.length > 0 ? myTrips.map((tripData) => (
                <div key={tripData.trip.id} className="border border-slate-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="p-2 sm:p-3 bg-teal-50 rounded-lg flex-shrink-0">
                          <Plane className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-1 sm:mb-2 truncate">
                            {tripData.trip.title}
                          </h3>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-600 mb-3">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{tripData.trip.destination}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4 flex-shrink-0" />
                              <span className="text-xs sm:text-sm">
                                {new Date(tripData.trip.start_date).toLocaleDateString('it-IT')} - 
                                {new Date(tripData.trip.end_date).toLocaleDateString('it-IT')}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate text-xs sm:text-sm">
                                Agente: {tripData.agent?.first_name} {tripData.agent?.last_name}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm sm:text-base text-slate-600 mb-4 line-clamp-2 sm:line-clamp-none">{tripData.trip.description}</p>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <Badge className={`${getStatusColor(tripData.trip.status)} text-xs sm:text-sm`}>
                              {getStatusLabel(tripData.trip.status)}
                            </Badge>
                            <Badge variant="outline" className="text-xs sm:text-sm">
                              {tripData.trip.trip_type}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex sm:flex-col gap-2 sm:ml-4">
                      <Link to={`/trips/${tripData.trip.id}`} className="flex-1 sm:flex-initial">
                        <Button size="sm" className="flex items-center justify-center gap-2 w-full text-xs sm:text-sm px-3 sm:px-4 py-2">
                          <Eye className="w-4 h-4" />
                          <span className="hidden sm:inline">Dettagli</span>
                          <span className="sm:hidden">Info</span>
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-12 sm:py-16">
                  <Plane className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-slate-400 mb-4 sm:mb-6" />
                  <h3 className="text-base sm:text-lg font-medium text-slate-800 mb-2">
                    Nessun viaggio ancora
                  </h3>
                  <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
                    Contatta la nostra agenzia per organizzare il tuo prossimo viaggio
                  </p>
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 sm:p-6 max-w-md mx-auto">
                    <h4 className="font-medium text-teal-800 mb-2 text-sm sm:text-base">Contattaci</h4>
                    <p className="text-xs sm:text-sm text-teal-700">
                      Parlaci delle tue destinazioni dei sogni e organizzeremo 
                      un viaggio perfetto per te!
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Componente Form per Richiesta Preventivo
const QuoteRequestForm = ({ quoteRequest, setQuoteRequest, onSubmit, onCancel, loading }) => {

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="destination">Destinazione *</Label>
          <Input
            id="destination"
            placeholder="Es: Parigi, Caraibi, Giappone..."
            value={quoteRequest.destination}
            onChange={(e) => setQuoteRequest({...quoteRequest, destination: e.target.value})}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="trip_type">Tipo di Viaggio</Label>
          <Select value={quoteRequest.trip_type} onValueChange={(value) => setQuoteRequest({...quoteRequest, trip_type: value})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cruise">Crociera</SelectItem>
              <SelectItem value="resort">Resort</SelectItem>
              <SelectItem value="tour">Tour</SelectItem>
              <SelectItem value="custom">Personalizzato</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="start_date">Data di Partenza *</Label>
          <Input
            id="start_date"
            type="date"
            value={quoteRequest.start_date}
            onChange={(e) => setQuoteRequest({...quoteRequest, start_date: e.target.value})}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="end_date">Data di Ritorno</Label>
          <Input
            id="end_date"
            type="date"
            value={quoteRequest.end_date}
            onChange={(e) => setQuoteRequest({...quoteRequest, end_date: e.target.value})}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="travelers">Numero Viaggiatori</Label>
          <Input
            id="travelers"
            type="number"
            min="1"
            max="20"
            value={quoteRequest.travelers}
            onChange={(e) => setQuoteRequest({...quoteRequest, travelers: parseInt(e.target.value)})}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="budget_range">Budget Indicativo</Label>
          <Select value={quoteRequest.budget_range} onValueChange={(value) => setQuoteRequest({...quoteRequest, budget_range: value})}>
            <SelectTrigger>
              <SelectValue placeholder="Seleziona budget..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="budget">Economico (meno di 1000 Euro)</SelectItem>
              <SelectItem value="medium">Medio (1000-3000 Euro)</SelectItem>
              <SelectItem value="premium">Premium (3000-5000 Euro)</SelectItem>
              <SelectItem value="luxury">Luxury (oltre 5000 Euro)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="contact_preference">Modalità di Contatto</Label>
          <Select value={quoteRequest.contact_preference} onValueChange={(value) => setQuoteRequest({...quoteRequest, contact_preference: value})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="phone">Telefono</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="special_requirements">Richieste Speciali</Label>
        <Textarea
          id="special_requirements"
          placeholder="Raccontaci i tuoi sogni di viaggio, preferenze, esigenze particolari..."
          rows={4}
          value={quoteRequest.special_requirements}
          onChange={(e) => setQuoteRequest({...quoteRequest, special_requirements: e.target.value})}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button onClick={onSubmit} disabled={loading}>
          <Send className="w-4 h-4 mr-2" />
          {loading ? 'Invio...' : 'Invia Richiesta'}
        </Button>
      </div>
    </div>
  );
};

export default ClientDashboard;